import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { messageTemplateService } from '../../../services/messageTemplateService';
import toast from 'react-hot-toast';

const TemplateLibrary = ({ userProfile }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'whatsapp',
    category: 'welcome',
    subject: '',
    content: '',
    variables: []
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await messageTemplateService.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Error al cargar plantillas');
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await messageTemplateService.createTemplate({
        ...formData,
        createdBy: userProfile?.id
      });
      toast.success('Plantilla creada exitosamente');
      setIsCreating(false);
      setFormData({
        name: '',
        type: 'whatsapp',
        category: 'welcome',
        subject: '',
        content: '',
        variables: []
      });
      loadTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error al crear plantilla');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('¿Estás seguro de eliminar esta plantilla?')) return;
    try {
      await messageTemplateService.deleteTemplate(templateId);
      toast.success('Plantilla eliminada');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error al eliminar plantilla');
    }
  };

  const types = messageTemplateService.getTypes();
  const categories = messageTemplateService.getCategories();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Layers" size={20} className="mr-2 text-primary" />
          Plantillas de Mensajes
        </h3>
        <button
          onClick={() => setIsCreating(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth flex items-center"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Nueva Plantilla
        </button>
      </div>

      {/* Create/Edit Form */}
      {isCreating && (
        <div className="bg-card rounded-xl p-6 border border-border">
          <h4 className="text-md font-semibold text-foreground mb-4">Nueva Plantilla</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Nombre *</label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Bienvenida Lead"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Tipo *</label>
                <select
                  value={formData?.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {types?.map(type => (
                    <option key={type?.value} value={type?.value}>{type?.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Categoría *</label>
                <select
                  value={formData?.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {formData?.type === 'email' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Asunto</label>
                <input
                  type="text"
                  value={formData?.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Asunto del email"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contenido *</label>
              <textarea
                value={formData?.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Usa {{variable}} para insertar variables dinámicas"
                rows={6}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Variables disponibles: {'{nombre}'}, {'{proyecto}'}, {'{asesor}'}, {'{monto}'}, {'{fecha}'}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsCreating(false)}
                className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateTemplate}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth"
              >
                Crear Plantilla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates?.map(template => (
          <div key={template?.id} className="bg-card rounded-xl p-6 border border-border hover:shadow-md transition-smooth">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
                  <Icon 
                    name={types?.find(t => t?.value === template?.type)?.icon || 'MessageCircle'} 
                    size={20} 
                    className="text-primary" 
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{template?.name}</h4>
                  <p className="text-xs text-muted-foreground capitalize">{template?.type}</p>
                </div>
              </div>
              <button
                onClick={() => handleDeleteTemplate(template?.id)}
                className="text-muted-foreground hover:text-error transition-smooth"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
            <div className="bg-muted/30 rounded-lg p-3 mb-3">
              <p className="text-xs text-foreground line-clamp-3">{template?.content}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded">
                {categories?.find(c => c?.value === template?.category)?.label}
              </span>
              <button
                onClick={() => setSelectedTemplate(template)}
                className="text-xs text-primary hover:underline"
              >
                Ver detalles
              </button>
            </div>
          </div>
        ))}
      </div>

      {templates?.length === 0 && !isCreating && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Layers" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay plantillas creadas</p>
          <button
            onClick={() => setIsCreating(true)}
            className="mt-4 text-primary hover:underline"
          >
            Crear primera plantilla
          </button>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;